import type { Language } from '@/types/translation'

/**
 * Copia locale dell'ultima risposta valida di lingue e traduzioni.
 *
 * Serve a far partire l'app con le stringhe giuste **prima** che l'API risponda:
 * senza questa copia, ogni avvio mostrerebbe per un istante l'inglese del bundle
 * a un utente italiano. La freschezza non è affidata a una scadenza ma al campo
 * `version` che l'API restituisce: si riusa la copia subito e si ricarica in
 * background solo se la versione è cambiata.
 *
 * L'elenco lingue è in cache per la stessa ragione: senza, con l'API irraggiungibile
 * il selettore mostrerebbe una sola voce inventata invece delle lingue vere.
 */

/** `v1` è la versione del formato: cambiarla invalida le copie vecchie. */
const PREFIX = 'travick.i18n.v1'
const LANGUAGES_KEY = `${PREFIX}.languages`

export interface CachedMessages {
  version: string | null
  messages: Record<string, string>
}

function storageKey(locale: string): string {
  return `${PREFIX}.${locale}`
}

export function readCachedMessages(locale: string): CachedMessages | null {
  try {
    const raw = window.localStorage.getItem(storageKey(locale))
    if (!raw) return null

    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null

    const { version, messages } = parsed as Partial<CachedMessages>
    if (!messages || typeof messages !== 'object') return null

    return {
      version: typeof version === 'string' ? version : null,
      messages: messages as Record<string, string>,
    }
  } catch {
    // JSON corrotto o storage non disponibile: si riparte dall'API.
    return null
  }
}

export function writeCachedMessages(locale: string, entry: CachedMessages): void {
  try {
    window.localStorage.setItem(storageKey(locale), JSON.stringify(entry))
  } catch {
    // Quota piena o storage negato: si perde solo l'avvio immediato.
  }
}

export function readCachedLanguages(): Language[] | null {
  try {
    const raw = window.localStorage.getItem(LANGUAGES_KEY)
    if (!raw) return null

    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null

    const languages = parsed.filter(
      (entry): entry is Language =>
        !!entry && typeof entry === 'object' && typeof (entry as Language).iso_code === 'string',
    )

    return languages.length > 0 ? languages : null
  } catch {
    return null
  }
}

export function writeCachedLanguages(languages: Language[]): void {
  try {
    window.localStorage.setItem(LANGUAGES_KEY, JSON.stringify(languages))
  } catch {
    // Non fatale, come sopra.
  }
}
